import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import Cookies from 'js-cookie';
import {
  Route,
  Redirect,
  withRouter,
} from 'react-router-dom';
import { Waypoint } from 'react-waypoint';
import ImageGallery from 'react-image-gallery';
import { injectIntl } from 'react-intl';

import {
  setSettings,
  setFileSizes,
} from '../actions/App';
import { fetchUserProfile } from '../actions/Auth';
import {
  clearFiles,
  setFilesLoading,
  setCollectionUuid,
  clearFilesList,
  setFilesParams,
  getFiles,
  setFilesEnd,
} from '../actions/Files';
import {
  getCollection,
  clearCollections,
  clearCollectionsParams,
  setCollectionsLoading,
  clearCollectionsList,
  setCollectionsParams,
} from '../actions/Collections';
import {
  authShare,
  fetchShare,
  fetchFiles,
  fetchCollections,
  getShare,
  setShareProvidePassword,
  fetchShareFileStatuses,
} from '../actions/Shares';
import { fetchTaxonomies, fetchMetadataGroups, fetchCustomFields } from '../actions/Metadata';

import { isLoggedIn } from '../api/Auth';
import { APIgetShareCollection } from '../api/Shares';

import File from './File';
import ShareHeader from '../components/Share/Header';
import ShareTitle from '../components/Share/Title';
import ShareViewActions from '../components/Share/ViewActions';
import FilesGrid from '../components/Files/Grid';
import FilesMasonry from '../components/Files/Masonry';
import FilesTile from '../components/Files/Tile';
import FilesSelectAll from '../components/Files/SelectAll';
import CollectionsGrid from '../components/Collections/Grid';
import CollectionsSelectAll from '../components/Collections/SelectAll';
import ConfirmationModal from '../components/ConfirmationModal';
import FlashMessages from '../components/FlashMessages';
import Downloads from '../components/Downloads';
import CollectionsBreadcrumbs from '../components/Collections/Breadcrumbs';
import Actionbar from '../components/Actionbar';
import ShareUnlockAccess from '../components/Share/UnlockAccess';
import TermsModalHandler from '../components/TermsModalHandler';

import Loading from '../components/Loading';
import LoadingMain from '../components/LoadingMain';
import NothingFound from '../components/NothingFound';
import PoweredBy from '../components/PoweredBy';

import { uuidPattern } from '../helpers';
import { cleanLoginParams } from '../helpers/App';
import { getFilePageUrl } from '../helpers/Files';
import { getDefaultSortParams } from '../helpers/Sort';

class Share extends Component {
  constructor(props) {
    super(props);

    this.state = {
      redirect: false,
      error: null,
      accessLocked: false,
    };

    this.imageGallery = React.createRef();

    this.previousLocation = props.location;
    this.setOrder = this.setOrder.bind(this);
    this.handleAuthShare = this.handleAuthShare.bind(this);
    this.handleFilesView = this.handleFilesView.bind(this);
    this.submitAccess = this.submitAccess.bind(this);
    this.checkAccess = this.checkAccess.bind(this);
  }

  componentDidMount() {
    this.fetchShare(true);
  }

  shouldComponentUpdate(nextProps, nextState) {
    const {
      share,
      filesLoading,
      collectionsLoading,
      location,
      files,
      collection,
      collections,
      providePassword,
      passwordError,
      authLoading,
      notFound,
      selectedFiles,
      selectedCollections,
    } = this.props;
    const {
      share: nextShare,
      filesLoading: nextFilesLoading,
      collectionsLoading: nextCollectionsLoading,
      location: nextLocation,
      files: nextFiles,
      collection: nextCollection,
      collections: nextCollections,
      providePassword: nextProvidePassword,
      passwordError: nextPasswordError,
      authLoading: nextAuthLoading,
      notFound: nextNotFound,
      selectedFiles: nextSelectedFiles,
      selectedCollections: nextSelectedCollections,
    } = nextProps;
    const {
      redirect,
      error,
    } = this.state;
    const {
      redirect: nextRedirect,
      error: nextError,
    } = nextState;

    if (files !== nextFiles) {
      return true;
    }

    if (share !== nextShare) {
      return true;
    }

    if (collections !== nextCollections && (!location.state || !location.state.modal)) {
      return true;
    }

    if (collection !== nextCollection && (!location.state || !location.state.modal)) {
      return true;
    }

    if (redirect !== nextRedirect) {
      return true;
    }

    if (filesLoading !== nextFilesLoading) {
      return true;
    }

    if (collectionsLoading !== nextCollectionsLoading) {
      return true;
    }

    if (location.pathname !== nextLocation.pathname) {
      return true;
    }

    if (error !== nextError) {
      return true;
    }

    if (providePassword !== nextProvidePassword) {
      return true;
    }

    if (passwordError !== nextPasswordError) {
      return true;
    }

    if (authLoading !== nextAuthLoading) {
      return true;
    }

    if (notFound !== nextNotFound) {
      return true;
    }

    if (selectedFiles.length !== nextSelectedFiles.length ||
      selectedCollections.length !== nextSelectedCollections.length) {
      return true;
    }

    return false;
  }

  componentDidUpdate(prevProps) {
    const {
      location,
      match,
      loadFiles,
      loadCollections,
      share,
      providePassword,
    } = this.props;
    const { collectionUuid } = match.params;
    const { collectionUuid: prevCollectionUuid } = prevProps.match.params;

    if (!location.state || !location.state.modal) {
      this.previousLocation = location;
    }

    if (collectionUuid !== prevCollectionUuid && share) {
      this.clearFilesAndCollections();
      this.fetchCollection();
      loadFiles();
      loadCollections();
    }

    if (prevProps.providePassword !== providePassword) {
      this.checkAccess();
    }

    if (prevProps.share !== share) {
      this.checkAccess();
    }
  }

  componentWillUnmount() {
    const {
      clearCollectionsAction,
      clearFilesAction,
      clearShareAction,
    } = this.props;

    clearShareAction();
    clearCollectionsAction();
    clearFilesAction();
  }

  getLoadedFilesLabel() {
    const {
      files,
      filesTotal,
    } = this.props;

    if (filesTotal === 0) {
      return null;
    }

    return `(${files.length} of ${filesTotal})`;
  }

  getLoadedCollectionsLabel() {
    const {
      collections,
      collectionsTotal,
    } = this.props;

    if (collectionsTotal === 0) {
      return null;
    }

    return `(${collections.length} of ${collectionsTotal})`;
  }

  clearFilesAndCollections() {
    const {
      clearFilesAction,
      clearCollectionsAction,
    } = this.props;

    clearFilesAction();
    clearCollectionsAction();
  }

  fetchCollection() {
    const {
      match,
      loadCollections,
      setCollectionAction,
      setCollectionUuidAction,
    } = this.props;

    const {
      collectionUuid,
      shareUuid,
    } = match.params;
    if (collectionUuid) {
      setCollectionUuidAction(collectionUuid);
      APIgetShareCollection(shareUuid, collectionUuid).then((response) => {
        setCollectionAction(response.data);
        loadCollections();
      });
    } else {
      setCollectionUuidAction(null);
      setCollectionAction(null);
    }
  }

  handleFilesView(view) {
    const { setFilesView } = this.props;

    setFilesView(view);
    this.fetchShare(false, view);
  }

  fetchShare(initial = false, viewParam = null) {
    const {
      match,
      filesParams,
      loadShare,
      loadCollections,
      loadFiles,
      loadMetadataGroups,
      loadTaxonomies,
      loadCustomFields,
      loadFileStatuses,
      setFilesLoadingAction,
      getFilesAction,
      setFilesEndAction,
      setFilesParamsAction,
    } = this.props;
    const { shareUuid } = match.params;

    const view = viewParam || filesParams.view;

    loadShare(shareUuid, { view }).then((response) => {
      if (response.uuid) {
        this.checkAccess();
        if (initial) {
          loadMetadataGroups();
          loadTaxonomies();
          loadCustomFields();
          loadFileStatuses(shareUuid);
        }
        if (response.view) {
          setFilesParamsAction({ view: response.view });
        }
        if (!response.files) {
          if (initial) {
            if (response.order) {
              setFilesParamsAction({ orderby: response.order[0], order: response.order[1] });
            } else if (response.top_collection) {
              setFilesParamsAction({ orderby: 'position', order: 'asc' });
            }
          }
          if (initial) {
            this.fetchCollection();
          } else {
            loadCollections();
          }
          loadFiles();
        } else {
          setFilesLoadingAction(true);
          getFilesAction(response.files);
          setFilesEndAction();
          setFilesLoadingAction(false);
        }
      }

      if (response.status === 403) {
        if (response.data.error === 'login_required') {
          this.setState({ redirect: true });
        } else if (response.data.error) {
          this.setState({ error: response.data.message });
        }
      }
    });
  }

  setOrder(orderby, order) {
    const { handleFilesParams } = this.props;

    handleFilesParams(orderby, order);
    this.fetchShare();
  }

  handleAuthShare(name, email, password) {
    const {
      share,
      performAuthShare,
    } = this.props;

    performAuthShare(share.uuid, { name, email, password }).then(() => {
      this.checkAccess();
    });
  }

  submitAccess(name = '', email = '', password = '') {
    this.handleAuthShare(name, email, password);
  }

  checkAccess() {
    const {
      share,
      providePassword,
    } = this.props;

    let locked = false;

    if (providePassword) {
      locked = true;
    }

    if (!isLoggedIn() && share && share.require_name) {
      const nameCookie = Cookies.get(`sp_share_user_name_${share.uuid}`);

      if (!nameCookie) {
        locked = true;
      }
    }

    if (!isLoggedIn() && share && share.require_email) {
      const emailCookie = Cookies.get(`sp_share_user_email_${share.uuid}`);

      if (!emailCookie) {
        locked = true;
      }
    }

    this.setState({
      accessLocked: locked,
    });
  }

  render() {
    const {
      intl,
      history,
      match,
      share,
      notFound,
      files,
      filesLoading,
      filesEnd,
      collection,
      collections,
      collectionsLoading,
      collectionsEnd,
      loadFiles,
      loadCollections,
      location,
      authLoading,
      reorderingMode,
      selectedFiles,
      selectedCollections,
    } = this.props;
    const {
      error,
      redirect,
      accessLocked,
    } = this.state;
    const { collectionUuid } = match.params;

    const showingActionbar = !reorderingMode &&
      (selectedFiles.length > 0 || selectedCollections.length > 0);

    if (redirect) {
      return (
        <Redirect
          to={{
            pathname: '/login',
            search: location.search,
            state: {
              from: `${location.pathname}${cleanLoginParams(location.search)}`,
            },
          }}
        />
      );
    }

    if (notFound) {
      return (
        <div className="layout layout-share">
          <ShareHeader
            share={share}
            notFound={notFound}
          />
          <NothingFound />
        </div>
      );
    }

    if (error) {
      return (
        <div className="layout layout-share">
          <ShareHeader
            share={share}
            notFound={true}
          />
          <p className="center">
            {error}
          </p>
        </div>
      );
    }

    if (!share) {
      return (
        <div className="layout layout-share">
          <LoadingMain />
        </div>
      );
    }

    if (accessLocked) {
      return (
        <ShareUnlockAccess
          onSubmit={this.submitAccess}
          loading={authLoading}
          checkAccess={this.checkAccess}
        />
      );
    }

    const isModal = !!((
      (
        location.state &&
        location.state.modal &&
        this.previousLocation !== location
      ) ||
      (
        location.pathname.match(`/shares/(${uuidPattern})/files/(${uuidPattern})`) ||
        location.pathname.match(`/shares/(${uuidPattern})/collections/(${uuidPattern})/files/(${uuidPattern})`)
      )
    ));

    const photos = share.files ? share.files.map(file => (
      {
        original: file.urls.featured,
        thumbnail: file.urls.thumbnail,
        description: file.name,
        url: getFilePageUrl({
          fileUuid: file.uuid,
          shareUuid: share.uuid,
          shareCollectionUuid: collectionUuid,
        }),
        originalAlt: file.display_name,
        thumbnailAlt: file.display_name,
      }
    )) : [];

    return (
      <div className="layout layout-share">
        <span id="download" hidden aria-hidden />
        <span id="edit" hidden aria-hidden />
        <div className={classnames('share-main', { 'with-actionbar': showingActionbar })}>
          <ShareHeader
            share={share}
            notFound={notFound}
          />
          <div className="share-content">
            <div className="share-list-header">
              <ShareTitle
                share={share}
              />
              <ShareViewActions
                share={share}
                setOrder={this.setOrder}
                setFilesView={this.handleFilesView}
              />
            </div>
            {collection && (
              <header className="page-header">
                <div className="page-header-left">
                  <CollectionsBreadcrumbs
                    collection={collection}
                    share={share}
                    showCollectionTitle={true}
                  />
                </div>
              </header>
            )}
            {share.view === 'slider' && (
              <div className="share-slider">
                <ImageGallery
                  additionalClass="slider-clickable"
                  ref={(ref) => { this.imageGallery = ref; }}
                  items={photos}
                  showPlayButton={false}
                  showFullscreenButton={false}
                  showThumbnails={photos.length > 1}
                  onClick={() => {
                    if (photos[this.imageGallery.getCurrentIndex()]) {
                      history.push(photos[this.imageGallery.getCurrentIndex()].url);
                    }
                  }}
                />
              </div>
            )}
            {(!share.view || ['grid', 'framed', 'masonry', 'tile'].includes(share.view)) && (
              <>
                <div className="collection-tools">
                  <div className="list-switch">
                    {collections.length > 0 && (
                      <span>
                        {intl.formatMessage({
                          defaultMessage: 'Collections {label}',
                        }, { label: this.getLoadedCollectionsLabel() })}{' '}
                      </span>
                    )}
                  </div>
                  {share.allow_download && (
                    <CollectionsSelectAll />
                  )}
                </div>
                {collections.length > 0 && (
                  <CollectionsGrid
                    className="shared-collections-grid"
                    collections={collections}
                    share={share}
                    {...(share.view ? { selectedView: share.view } : {})}
                  />
                )}
                {(!collectionsLoading && !collectionsEnd) && (
                  <Waypoint
                    bottomOffset="-200px"
                    onEnter={() => {
                      if (!collectionsEnd) {
                        loadCollections();
                      }
                    }}
                  />
                )}
                <div className="collection-tools">
                  <div className="list-switch">
                    {files.length > 0 && (
                      <span>
                        {intl.formatMessage({
                          defaultMessage: 'Files {label}',
                        }, { label: this.getLoadedFilesLabel() })}{' '}
                      </span>
                    )}
                  </div>
                  {share.allow_download && (
                    <FilesSelectAll />
                  )}
                </div>
                {files.length > 0 && (
                  <>
                    {(!share.view || ['grid', 'framed'].includes(share.view)) && (
                      <FilesGrid
                        className="shared-files-grid shared-files-list"
                        files={files}
                        shareUuid={share.uuid}
                        share={share}
                        shareCollectionUuid={collectionUuid}
                        selectedView={share.view}
                      />
                    )}
                    {share.view === 'masonry' && (
                      <FilesMasonry
                        className="shared-files-masonry shared-files-list"
                        files={files}
                        shareUuid={share.uuid}
                        share={share}
                        shareCollectionUuid={collectionUuid}
                      />
                    )}
                    {share.view === 'tile' && (
                      <FilesTile
                        className="shared-files-tile shared-files-list"
                        files={files}
                        shareUuid={share.uuid}
                        share={share}
                        shareCollectionUuid={collectionUuid}
                      />
                    )}
                  </>
                )}
                {(
                  !filesLoading &&
                  !collectionsLoading &&
                  filesEnd &&
                  files.length === 0 &&
                  collections.length === 0
                ) && (
                  <NothingFound />
                )}
                {(filesLoading && !filesEnd) ? (
                  <Loading />
                ) : (
                  <Waypoint
                    bottomOffset="-200px"
                    onEnter={() => {
                      if (!filesEnd) {
                        loadFiles();
                      }
                    }}
                  />
                )}
              </>
            )}
          </div>
        </div>
        {(selectedFiles.length === 0 && selectedCollections.length === 0) && (
          <PoweredBy />
        )}
        {(isModal && files.length > 0) ? (
          <Route
            path="/shares/:shareUuid/collections/:collectionUuid/files/:fileId"
            component={() => (
              <File
                originalLocation={this.previousLocation}
                shareUuid={share.uuid}
                shareCollectionUuid={collectionUuid}
              />
            )}
          />
        ) : (
          null
        )}
        {(isModal && (files.length > 0 || share.files?.length > 0)) ? (
          <Route
            path="/shares/:shareUuid/files/:fileId"
            component={() => (
              <File originalLocation={this.previousLocation} shareUuid={share.uuid} />
            )}
          />
        ) : (
          null
        )}
        <ConfirmationModal />
        <Downloads />
        <FlashMessages />
        <TermsModalHandler share={share} />
        {(share && share.allow_download) && (
          <Actionbar />
        )}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  share: state.Shares.share,
  authLoading: state.Shares.authLoading,
  notFound: state.Shares.notFound,
  providePassword: state.Shares.providePassword,
  passwordError: state.Shares.passwordError,
  files: state.Files.files,
  filesTotal: state.Files.filesTotal,
  filesLoading: state.Files.loading,
  filesEnd: state.Files.end,
  filesParams: state.Files.params,
  collection: state.Collections.collection,
  collections: state.Collections.collections,
  collectionsTotal: state.Collections.collectionsTotal,
  collectionsLoading: state.Collections.collectionsLoading,
  collectionsEnd: state.Collections.collectionsEnd,
  reorderingMode: state.Files.reorderingMode,
  selectedFiles: state.Files.selectedFiles,
  selectedCollections: state.Collections.selectedCollections,
});

const mapDispatchToProps = dispatch => ({
  loadUserProfile: () => {
    dispatch(fetchUserProfile());
  },
  loadShare: (shareUuid, view) => (
    dispatch(fetchShare(shareUuid, view)).then((response) => {
      if (response.client) {
        dispatch(setSettings(response.client.settings));
        dispatch(setFileSizes(response.client.file_sizes));
      }
      if (response.provide_password) {
        dispatch(setShareProvidePassword(true));
      }
      return response;
    })
  ),
  loadFiles: () => (
    dispatch(fetchFiles())
  ),
  loadCollections: () => (
    dispatch(fetchCollections())
  ),
  loadMetadataGroups: () => (
    dispatch(fetchMetadataGroups({ share: true }))
  ),
  loadTaxonomies: () => (
    dispatch(fetchTaxonomies({ share: true }))
  ),
  loadCustomFields: () => (
    dispatch(fetchCustomFields({ share: true }))
  ),
  loadFileStatuses: (shareUuid) => {
    dispatch(fetchShareFileStatuses(shareUuid));
  },
  performAuthShare: (shareId, data) => (
    dispatch(authShare(shareId, data)).then((response) => {
      if (response && response.status !== 401) {
        dispatch(fetchFiles());
        dispatch(fetchCollections());
      }
    })
  ),
  clearFilesAction: () => {
    dispatch(setFilesParams({ page: 1 }));
    dispatch(clearFiles());
  },
  clearCollectionsAction: () => {
    const sortParams = getDefaultSortParams('collections', 'position', 'asc');
    dispatch(clearCollectionsParams(sortParams));
    dispatch(clearCollections(sortParams));
  },
  clearShareAction: () => {
    dispatch(getShare(null));
  },
  setFilesLoadingAction: (loading) => {
    dispatch(setFilesLoading(loading));
  },
  getFilesAction: (files) => {
    dispatch(getFiles(files));
  },
  setFilesEndAction: () => {
    dispatch(setFilesEnd(true));
  },
  setCollectionsLoadingAction: (loading) => {
    dispatch(setCollectionsLoading(loading));
  },
  setCollectionUuidAction: (collectionUuid) => {
    dispatch(setCollectionUuid(collectionUuid));
  },
  setCollectionAction: (collection) => {
    dispatch(getCollection(collection));
  },
  handleFilesParams: (orderby, order) => {
    dispatch(setCollectionsParams({
      orderby,
      order,
      page: 0,
    }));
    dispatch(setFilesParams({
      orderby,
      order,
      page: 0,
    }));
    dispatch(clearCollectionsList());
    dispatch(clearFilesList());
  },
  setFilesParamsAction: (params) => {
    dispatch(setFilesParams(params));
  },
  setFilesView: (view) => {
    dispatch(clearCollectionsList());
    dispatch(clearFilesList());
    dispatch(setFilesParams({ view }));
  },
});

Share.propTypes = {
  intl: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  share: PropTypes.object,
  authLoading: PropTypes.bool.isRequired,
  notFound: PropTypes.bool.isRequired,
  providePassword: PropTypes.bool.isRequired,
  passwordError: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  files: PropTypes.array.isRequired,
  filesTotal: PropTypes.number.isRequired,
  filesLoading: PropTypes.bool.isRequired,
  filesEnd: PropTypes.bool.isRequired,
  filesParams: PropTypes.object.isRequired,
  collection: PropTypes.object,
  collections: PropTypes.array.isRequired,
  collectionsTotal: PropTypes.number.isRequired,
  collectionsLoading: PropTypes.bool.isRequired,
  collectionsEnd: PropTypes.bool.isRequired,
  loadShare: PropTypes.func.isRequired,
  loadFiles: PropTypes.func.isRequired,
  loadCollections: PropTypes.func.isRequired,
  loadMetadataGroups: PropTypes.func.isRequired,
  loadTaxonomies: PropTypes.func.isRequired,
  loadCustomFields: PropTypes.func.isRequired,
  loadFileStatuses: PropTypes.func.isRequired,
  clearFilesAction: PropTypes.func.isRequired,
  setFilesLoadingAction: PropTypes.func.isRequired,
  getFilesAction: PropTypes.func.isRequired,
  setFilesEndAction: PropTypes.func.isRequired,
  clearCollectionsAction: PropTypes.func.isRequired,
  setCollectionUuidAction: PropTypes.func.isRequired,
  setCollectionAction: PropTypes.func.isRequired,
  clearShareAction: PropTypes.func.isRequired,
  performAuthShare: PropTypes.func.isRequired,
  handleFilesParams: PropTypes.func.isRequired,
  setFilesView: PropTypes.func.isRequired,
  setFilesParamsAction: PropTypes.func.isRequired,
  reorderingMode: PropTypes.bool.isRequired,
  selectedFiles: PropTypes.array.isRequired,
  selectedCollections: PropTypes.array.isRequired,
};

Share.defaultProps = {
  share: null,
  collection: null,
};

export default withRouter(injectIntl(connect(mapStateToProps, mapDispatchToProps)(Share)));
